import {
  Dispatch,
  FC,
  ReactNode,
  createContext,
  useContext,
  useEffect,
  useMemo,
  useReducer,
} from 'react';
import { TDocument } from '../../../../apis/documents/documents.interfaces';
import { useProfilePersonalDocumentsHook } from '../hooks/useProfilePersonalDocumentsHook';
import { localStorageGet } from '../../../../utils/localStorage';
import { ILoggedInUser } from '../../../../apis/auth/auth.interfaces';
import { useQueryClient } from '@tanstack/react-query';
import isAppWithMock from 'src/utils/isAppWithMock';
import { TPaginationParams } from '../../../../apis/__shared/shared.interfaces';

type TProfileDocumentsState = {
  documents: TDocument[];
  selectedDocument: TDocument | null;
  editedDocument: TDocument | null;
  isDocumentLoading: boolean;
  documentErrorMessage: string;
  isEditMode: boolean;
  isCreateMode: boolean;
  isOpenAddDocumentDialog: boolean;
  isLoading: boolean;
  errorMessage?: string;
  totalCount: number;
  userParams: TPaginationParams;
};

enum ACTION_TYPE {
  SET_DOCUMENTS = 'SET_DOCUMENTS',
  SET_SELECTED_DOCUMENT = 'SET_SELECTED_DOCUMENT',
  SET_EDITED_DOCUMENT = 'SET_EDITED_DOCUMENT',
  SET_IS_DOCUMENT_LOADING = 'SET_IS_DOCUMENT_LOADING',
  SET_DOCUMENT_ERROR_MESSAGE = 'SET_DOCUMENT_ERROR_MESSAGE',
  SET_IS_EDIT_MODE = 'SET_IS_EDIT_MODE',
  SET_IS_CREATE_MODE = 'SET_IS_CREATE_MODE',
  SET_IS_OPEN_ADD_DOCUMENT_DIALOG = 'SET_IS_OPEN_ADD_DOCUMENT_DIALOG',
  ADD_DOCUMENT = 'ADD_DOCUMENT',
  UPDATE_DOCUMENT = 'UPDATE_DOCUMENT',
  REMOVE_DOCUMENT = 'REMOVE_DOCUMENT',
  SET_REQUEST_PARAMS = 'SET_REQUEST_PARAMS',
  FETCHING = 'FETCHING',
  FETCH_DATA_FAILURE = 'FETCH_DATA_FAILURE',
}

type Action =
  | {
      type: ACTION_TYPE.SET_DOCUMENTS;
      payload: { documents: TDocument[]; total: number };
    }
  | { type: ACTION_TYPE.SET_SELECTED_DOCUMENT; payload: TDocument | null }
  | { type: ACTION_TYPE.SET_EDITED_DOCUMENT; payload: TDocument | null }
  | { type: ACTION_TYPE.SET_IS_DOCUMENT_LOADING; payload: boolean }
  | { type: ACTION_TYPE.SET_DOCUMENT_ERROR_MESSAGE; payload: string }
  | { type: ACTION_TYPE.SET_IS_EDIT_MODE; payload: boolean }
  | { type: ACTION_TYPE.SET_IS_CREATE_MODE; payload: boolean }
  | { type: ACTION_TYPE.SET_IS_OPEN_ADD_DOCUMENT_DIALOG; payload: boolean }
  | { type: ACTION_TYPE.ADD_DOCUMENT; payload: TDocument }
  | { type: ACTION_TYPE.UPDATE_DOCUMENT; payload: TDocument }
  | { type: ACTION_TYPE.REMOVE_DOCUMENT; payload: string }
  | { type: ACTION_TYPE.SET_REQUEST_PARAMS; payload: TPaginationParams }
  | { type: ACTION_TYPE.FETCHING; payload: boolean }
  | { type: ACTION_TYPE.FETCH_DATA_FAILURE; payload: string };

const initialState: TProfileDocumentsState = {
  documents: [],
  selectedDocument: null,
  editedDocument: null,
  isDocumentLoading: false,
  documentErrorMessage: '',
  isEditMode: false,
  isCreateMode: false,
  isOpenAddDocumentDialog: false,
  isLoading: false,
  errorMessage: undefined,
  totalCount: 0,
  userParams: { page: 1, limit: 10 },
};

const ProfileDocumentsContext = createContext<
  TProfileDocumentsState | undefined
>(undefined);
const ProfileDocumentsDispatchContext = createContext<
  Dispatch<Action> | undefined
>(undefined);

const profileDocumentsReducer = (
  state: TProfileDocumentsState,
  action: Action
): TProfileDocumentsState => {
  switch (action.type) {
    case ACTION_TYPE.SET_DOCUMENTS:
      return {
        ...state,
        documents: action.payload.documents || [],
        totalCount: action.payload.total ?? 0,
        errorMessage: undefined,
      };
    case ACTION_TYPE.SET_SELECTED_DOCUMENT:
      return {
        ...state,
        selectedDocument: action.payload,
      };
    case ACTION_TYPE.SET_EDITED_DOCUMENT:
      return {
        ...state,
        editedDocument: action.payload,
      };
    case ACTION_TYPE.SET_IS_DOCUMENT_LOADING:
      return {
        ...state,
        isDocumentLoading: action.payload,
      };
    case ACTION_TYPE.SET_DOCUMENT_ERROR_MESSAGE:
      return {
        ...state,
        documentErrorMessage: action.payload,
      };
    case ACTION_TYPE.SET_IS_EDIT_MODE:
      return {
        ...state,
        isEditMode: action.payload,
        isCreateMode: false,
      };
    case ACTION_TYPE.SET_IS_CREATE_MODE:
      return {
        ...state,
        isCreateMode: action.payload,
        isEditMode: false,
      };
    case ACTION_TYPE.SET_IS_OPEN_ADD_DOCUMENT_DIALOG:
      return {
        ...state,
        isOpenAddDocumentDialog: action.payload,
      };
    case ACTION_TYPE.ADD_DOCUMENT:
      return {
        ...state,
        documents: [...state.documents, action.payload],
      };
    case ACTION_TYPE.UPDATE_DOCUMENT:
      return {
        ...state,
        documents: state.documents.map((doc) =>
          doc.documentId === action.payload.documentId ? action.payload : doc
        ),
        selectedDocument:
          state.selectedDocument?.documentId === action.payload.documentId
            ? action.payload
            : state.selectedDocument,
      };
    case ACTION_TYPE.REMOVE_DOCUMENT:
      return {
        ...state,
        documents: state.documents.filter(
          (doc) => doc.documentId !== action.payload
        ),
        selectedDocument:
          state.selectedDocument?.documentId === action.payload
            ? null
            : state.selectedDocument,
      };
    case ACTION_TYPE.SET_REQUEST_PARAMS:
      return {
        ...state,
        userParams: action.payload,
      };
    case ACTION_TYPE.FETCHING:
      return {
        ...state,
        isLoading: action.payload,
        errorMessage: action.payload ? undefined : state.errorMessage,
      };
    case ACTION_TYPE.FETCH_DATA_FAILURE:
      return {
        ...state,
        isLoading: false,
        errorMessage: action.payload,
      };
    default:
      throw new Error('Unhandled action type');
  }
};

export const ProfileDocumentsProvider: FC<{ children: ReactNode }> = ({
  children,
}) => {
  const [state, dispatch] = useReducer(profileDocumentsReducer, initialState);

  const withMockData = isAppWithMock();

  const userFromStorage = localStorageGet('loggedInUser', undefined) as
    | ILoggedInUser
    | undefined;
  const loggedInUserId = withMockData
    ? '1234567890'
    : userFromStorage?.id || '';

  const {
    data: documentsResponse,
    isLoading,
    error,
  } = useProfilePersonalDocumentsHook(loggedInUserId, state.userParams);

  useEffect(() => {
    dispatch({ type: ACTION_TYPE.FETCHING, payload: isLoading });
  }, [isLoading]);

  useEffect(() => {
    if (documentsResponse) {
      dispatch({
        type: ACTION_TYPE.SET_DOCUMENTS,
        payload: {
          documents: documentsResponse.data,
          total: documentsResponse.total,
        },
      });
    }
  }, [documentsResponse]);

  useEffect(() => {
    if (error) {
      dispatch({
        type: ACTION_TYPE.FETCH_DATA_FAILURE,
        payload: error?.response?.data?.message || 'Failed to fetch documents',
      });
    }
  }, [error]);

  const value = useMemo(
    () => ({
      ...state,
      isLoading,
    }),
    [state, isLoading]
  );

  return (
    <ProfileDocumentsContext.Provider value={value}>
      <ProfileDocumentsDispatchContext.Provider value={dispatch}>
        {children}
      </ProfileDocumentsDispatchContext.Provider>
    </ProfileDocumentsContext.Provider>
  );
};

export const useProfileDocumentsState = () => {
  const context = useContext(ProfileDocumentsContext);
  if (context === undefined) {
    throw new Error(
      'useProfileDocumentsState must be used within a ProfileDocumentsProvider'
    );
  }
  return context;
};

export const useProfileDocumentsActions = () => {
  const dispatch = useContext(ProfileDocumentsDispatchContext);
  const queryClient = useQueryClient();
  // const userFromStorage = localStorageGet('loggedInUser', undefined) as
  //   | ILoggedInUser
  //   | undefined;
  // const withMockData = process.env.REACT_APP_WITH_MOCK === 'true';
  // const loggedInUserId = withMockData
  //   ? '1234567890'
  //   : userFromStorage?.id || '';

  if (dispatch === undefined) {
    throw new Error(
      'useProfileDocumentsActions must be used within a ProfileDocumentsProvider'
    );
  }

  const refreshDocuments = () => {
    queryClient.invalidateQueries({
      queryKey: ['get-profile-personal-documents'],
    });
  };

  const updateSearchParams = (params: TPaginationParams) => {
    dispatch({ type: ACTION_TYPE.SET_REQUEST_PARAMS, payload: params });
  };

  const setSelectedDocument = (document: TDocument | null) => {
    dispatch({ type: ACTION_TYPE.SET_SELECTED_DOCUMENT, payload: document });
  };

  const setEditedDocument = (document: TDocument | null) => {
    dispatch({ type: ACTION_TYPE.SET_EDITED_DOCUMENT, payload: document });
  };

  const setIsDocumentLoading = (isLoading: boolean) => {
    dispatch({ type: ACTION_TYPE.SET_IS_DOCUMENT_LOADING, payload: isLoading });
  };

  const setDocumentErrorMessage = (errorMessage: string) => {
    dispatch({
      type: ACTION_TYPE.SET_DOCUMENT_ERROR_MESSAGE,
      payload: errorMessage,
    });
  };

  const setIsEditMode = (isEditMode: boolean) => {
    dispatch({ type: ACTION_TYPE.SET_IS_EDIT_MODE, payload: isEditMode });
  };

  const setIsCreateMode = (isCreateMode: boolean) => {
    dispatch({ type: ACTION_TYPE.SET_IS_CREATE_MODE, payload: isCreateMode });
  };

  const setIsOpenAddDocumentDialog = (isOpen: boolean) => {
    dispatch({
      type: ACTION_TYPE.SET_IS_OPEN_ADD_DOCUMENT_DIALOG,
      payload: isOpen,
    });
  };

  const addDocument = (document: TDocument) => {
    dispatch({ type: ACTION_TYPE.ADD_DOCUMENT, payload: document });
    // TODO: Call API to create document when service is available
    // After successful API call, refresh documents
    refreshDocuments();
  };

  const updateDocument = (document: TDocument) => {
    dispatch({ type: ACTION_TYPE.UPDATE_DOCUMENT, payload: document });
    // TODO: Call API to update document when service is available
    // After successful API call, refresh documents
    refreshDocuments();
  };

  const removeDocument = (documentId: string) => {
    dispatch({ type: ACTION_TYPE.REMOVE_DOCUMENT, payload: documentId });
    // TODO: Call API to delete document when service is available
    // After successful API call, refresh documents
    refreshDocuments();
  };

  return {
    refreshDocuments,
    updateSearchParams,
    setSelectedDocument,
    setEditedDocument,
    setIsDocumentLoading,
    setDocumentErrorMessage,
    setIsEditMode,
    setIsCreateMode,
    setIsOpenAddDocumentDialog,
    addDocument,
    updateDocument,
    removeDocument,
  };
};
